<template>
  <QuickAdds
    :bundles="quickAddBundles"
    :disabled="cartStore.mutationsBlocked"
    @add="handleAdd"
  />
</template>

<script lang="ts">
import { defineComponent } from 'vue'

import { useCartStore } from '@/stores/cart'
import { useMainStore } from '@/stores/main'
import { mapStores } from 'pinia'

import { substitute } from '@/composables/contentful'
import { useGtmTracking } from '@/composables/gtmTracking'
import { useCartMutationFeedback } from '@/composables/useCartMutationFeedback'
import { useQuickAdds } from '@/composables/useQuickAdds'
import { routeName } from '@/router/routeName'

import { CatalogBundleStorefrontDtoWithContent } from '@/types/CatalogBundleStorefrontDtoWithContent'

import { BundleStorefrontDto } from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

import QuickAdds from './QuickAdds.vue'
import { feedbackContent } from '@/content/feedback'

// Steps that come after Select Poses (`CART_CONFIGURE`) — i.e. the in-flow configuration step has
// already passed. Adding a configurable cross-sell on one of these routes the user back to Select
// Poses to configure it (the YMAL-release end-around for the claim modal); on earlier steps the
// normal pose-selection flow configures it in place, so no redirect.
const POST_SELECT_POSES_ROUTES: routeName[] = [routeName.RETOUCHING, routeName.CART_CHECKOUT]

/**
 * Cart-flyout placement of the YMAL Quick Adds. Pulls the gap-aware selection from `useQuickAdds` and
 * renders the presentational `QuickAdds` (which self-hides when empty); the add CTA adds the cross-sell
 * bundle to the cart, reusing the reward-crossing guard + GTM pattern from `useBundleListItem` (the
 * generic add toast is suppressed when the add crosses a reward tier — the reward toast covers it).
 * If the added bundle needs configuration and the user is past Select Poses, they're routed back there
 * to configure it (the claim-modal end-around). Mounted in `AppCartSidebar` below the cart items.
 */
export default defineComponent({
  name: 'QuickAddsFlyout',
  components: { QuickAdds },
  setup() {
    return {
      ...useQuickAdds(),
      ...useGtmTracking(),
      cartMutationFeedback: useCartMutationFeedback()
    }
  },
  computed: {
    ...mapStores(useCartStore, useMainStore)
  },
  methods: {
    async handleAdd(bundle: CatalogBundleStorefrontDtoWithContent) {
      await this.cartMutationFeedback.run({
        mutate: () =>
          this.cartStore.addCartBundle(
            bundle.catalogBundleId,
            bundle.bundle as BundleStorefrontDto,
            1
          ),
        onSuccess: () => {
          this.trackGtmEvent('add_to_cart', {
            ecommerce: {
              currency: 'USD',
              value: this.cartStore.lastAddedCartBundlesAsGoogleAnalyticsCartItems.reduce(
                (acc, item) => acc + item.price * item.quantity,
                0
              ),
              items: this.cartStore.lastAddedCartBundlesAsGoogleAnalyticsCartItems
            }
          })

          // End-around for the claim modal (YMAL-only release): if the added cross-sell requires
          // configuration and the user is already past Select Poses, route them back there to
          // configure it via the existing in-flow surface rather than surfacing a modal.
          if (
            this.cartStore.newBundlesNeedingConfiguration.length > 0 &&
            POST_SELECT_POSES_ROUTES.some((name) => name === this.$route.name)
          ) {
            this.mainStore.showCartSidebar = false
            this.$router.push({ name: routeName.CART_CONFIGURE })
          }
        },
        successMessage: substitute(feedbackContent.addedToCartTemplate, {
          BundleName: bundle.content.name
        }),
        errorMessage: substitute(feedbackContent.addToCartFailedTemplate, {
          BundleName: bundle.content.name
        })
      })
    }
  }
})
</script>
